import React, { useState, useEffect } from 'react';
import { countriesApi, Country, CreateCountryRequest } from '../api/countries';
import { Plus, Edit2, Trash2, Eye } from 'lucide-react';
import Modal from '../components/common/Modal';

function CountriesPage() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [viewingCountry, setViewingCountry] = useState<Country | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateCountryRequest>({
    code: '',
    name: '',
    phoneCode: '',
    currencyCode: ''
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await countriesApi.getAll();
      setCountries(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = {
      code: formData.code.trim(),
      name: formData.name.trim()
    };

    const emptyFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => {
        const fieldNames: { [key: string]: string } = {
          code: 'Country Code',
          name: 'Country Name'
        };
        return fieldNames[key];
      });

    if (emptyFields.length > 0) {
      setError(`Please fill in required fields: ${emptyFields.join(', ')}`);
      setFieldErrors(new Set(emptyFields));
      return;
    }
    
    setFieldErrors(new Set());

    try {
      setSubmitting(true);
      setError('');
      
      if (editingCountry) {
        await countriesApi.update(editingCountry.id, formData);
      } else {
        await countriesApi.create(formData);
      }
      
      setFormData({ code: '', name: '', phoneCode: '', currencyCode: '' });
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingCountry(null);
      await fetchCountries();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || `Failed to ${editingCountry ? 'update' : 'create'} country`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({
      code: country.code,
      name: country.name,
      phoneCode: country.phoneCode || '',
      currencyCode: country.currencyCode || ''
    });
    setShowEditModal(true);
  };

  const handleView = (country: Country) => {
    setViewingCountry(country);
    setShowViewModal(true);
  };

  const handleDelete = async (country: Country) => {
    const message = `Are you sure you want to ${country.isActive ? 'deactivate' : 'activate'} ${country.name}?`;
    if (!window.confirm(message)) {
      return;
    }

    try {
      await countriesApi.changeStatus(country.id, !country.isActive);
      await fetchCountries();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update country status');
    }
  };

  const getFieldClassName = (fieldName: string) => {
    const baseClass = "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
    const errorClass = fieldErrors.has(fieldName) 
      ? "border-red-300 bg-red-50" 
      : "border-gray-300";
    return `${baseClass} ${errorClass}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Countries</h1>
          <p className="text-gray-600">Manage countries and their settings</p>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Countries</h1>
        <p className="text-gray-600">Manage countries and their settings</p>
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">All Countries</h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Total: {countries.length} countries</span>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Country
              </button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {countries.map((country) => (
                <tr key={country.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {country.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {country.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {country.phoneCode || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {country.currencyCode || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      country.isActive 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {country.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(country)}
                        className="inline-flex items-center px-2 py-1 text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(country)}
                        className="inline-flex items-center px-2 py-1 text-gray-600 hover:text-gray-900"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(country)}
                        className={`inline-flex items-center px-2 py-1 ${
                          country.isActive 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Country Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormData({ code: '', name: '', phoneCode: '', currencyCode: '' });
          setError('');
          setFieldErrors(new Set());
        }}
        title="Add New Country"
        size="md"
      >
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Code *
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => {
                setFormData({ ...formData, code: e.target.value.toUpperCase() });
                if (fieldErrors.has('Country Code')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Country Code');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Country Code')}
              placeholder="e.g., US, GB, DE"
              maxLength={2}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (fieldErrors.has('Country Name')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Country Name');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Country Name')}
              placeholder="e.g., United States, United Kingdom"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Code
            </label>
            <input
              type="text"
              value={formData.phoneCode}
              onChange={(e) => setFormData({ ...formData, phoneCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., +1, +44"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency Code
            </label>
            <input
              type="text"
              value={formData.currencyCode}
              onChange={(e) => setFormData({ ...formData, currencyCode: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., USD, GBP, EUR"
              maxLength={3}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                setFormData({ code: '', name: '', phoneCode: '', currencyCode: '' });
                setError('');
                setFieldErrors(new Set());
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Country'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Country Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCountry(null);
          setFormData({ code: '', name: '', phoneCode: '', currencyCode: '' });
          setError('');
          setFieldErrors(new Set());
        }}
        title="Edit Country"
        size="md"
      >
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Code *
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => {
                setFormData({ ...formData, code: e.target.value.toUpperCase() });
                if (fieldErrors.has('Country Code')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Country Code');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Country Code')}
              placeholder="e.g., US, GB, DE"
              maxLength={2}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (fieldErrors.has('Country Name')) {
                  setFieldErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('Country Name');
                    return newSet;
                  });
                }
              }}
              className={getFieldClassName('Country Name')}
              placeholder="e.g., United States, United Kingdom"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Code
            </label>
            <input
              type="text"
              value={formData.phoneCode}
              onChange={(e) => setFormData({ ...formData, phoneCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., +1, +44"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency Code
            </label>
            <input
              type="text"
              value={formData.currencyCode}
              onChange={(e) => setFormData({ ...formData, currencyCode: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., USD, GBP, EUR"
              maxLength={3}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                setEditingCountry(null);
                setFormData({ code: '', name: '', phoneCode: '', currencyCode: '' });
                setError('');
                setFieldErrors(new Set());
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Updating...' : 'Update Country'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Country Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingCountry(null);
        }}
        title="Country Details"
        size="md"
      >
        {viewingCountry && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Country Code</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{viewingCountry.code}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Country Name</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{viewingCountry.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Phone Code</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{viewingCountry.phoneCode || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Currency Code</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{viewingCountry.currencyCode || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  viewingCountry.isActive 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {viewingCountry.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">ID</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{viewingCountry.id}</p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingCountry(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CountriesPage;
